// crewbit-admin/src/hooks/useBootstrapDefaults.ts
//
// Tier 2 Phase A §WorldClass.4 — onboarding-as-defaults-source substrate.
// Admin-side paired hook for `bootstrap_business_defaults` RPC (Migration 272).
// Mirrors crewbit-mobile/hooks/useBootstrapDefaults.ts.
//
// Owner-only (server-enforced). Resolves smart defaults from
// (province × trade × tax_year) → seeds business_financial_settings + writes
// businesses.activity_class / cra_business_number with COALESCE-preserve semantics.
// Emits MONEY_MUTATION_EVENT on success per `feedback_emit_via_inheritance.md`
// (web runtime: window.dispatchEvent).
//
// Routed through callMoneyRpc per MONEY_MODULE_WIRING_v1.md §2.1.

import { useCallback } from 'react';
import { callMoneyRpc, type MoneyRpcResult } from '../lib/moneyRpc';
import { MONEY_MUTATION_EVENT } from './useFinancialSummary';
import type { BootstrapDefaultsDerived } from '../types/onboarding';

export interface BootstrapDefaultsArgs {
  businessId: string;
  province: string;
  tradeKey: string;
  craBusinessNumber?: string | null;
  taxYear?: number | null;
}

export type BootstrapDefaultsHookResult = MoneyRpcResult<{
  business_id: string;
  derived: BootstrapDefaultsDerived;
}>;

export function useBootstrapDefaults() {
  return useCallback(
    async (args: BootstrapDefaultsArgs): Promise<BootstrapDefaultsHookResult> => {
      const result = await callMoneyRpc<{
        business_id: string;
        derived: BootstrapDefaultsDerived;
      }>('bootstrap_business_defaults', {
        p_business_id: args.businessId,
        p_province: args.province,
        p_trade_key: args.tradeKey,
        p_cra_business_number: args.craBusinessNumber ?? null,
        p_tax_year: args.taxYear ?? null,
      });
      if (result.success) {
        window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
      }
      return result;
    },
    [],
  );
}
